import type { ReactNode } from 'react';
import { motion, useDragControls } from 'framer-motion';
import { useRef } from 'react';
import { createPortal } from 'react-dom';

import Button from '../Input2/Button';
import { cn } from '../cn';
import usePortal from '../hooks/usePortal';
import { General, Layout } from '../StrokeIcons';

export interface FloatingWidgetProps {
  children: ReactNode;
  /** Renders the X in the drag-handle strip when provided. */
  onClose?: () => void;
  /** Tooltip for the X (e.g. "End call and close" while a call is live). */
  closeTooltip?: string;
  className?: string;
  'data-test'?: string;
}

/**
 * A non-modal, draggable floating card portaled to `document.body`. Unlike `Popup` there is no
 * backdrop and no `popupShown` body class — the page underneath stays fully visible and
 * interactive. Dragging starts only from the top handle strip (`dragListener={false}`), so the
 * card body's buttons never turn a press-and-slip into a drag.
 *
 * The fullscreen `pointer-events-none` wrapper doubles as the drag-constraints box, clamping the
 * card to the viewport. Constraints re-measure at each drag start, so after an extreme viewport
 * shrink the card may sit partially offscreen until the next drag re-clamps it — acceptable, the
 * handle anchors to the card's top edge and stays reachable.
 */
export function FloatingWidget({
  children,
  onClose,
  closeTooltip,
  className,
  'data-test': dataTest,
}: FloatingWidgetProps) {
  const constraintsRef = useRef<HTMLDivElement>(null);
  const dragControls = useDragControls();
  const target = usePortal('poseidon-floating-portal');

  return createPortal(
    // z-9000 keeps real modals (Popup at z-9998) stacked above the widget. The flex box centers
    // the card horizontally and anchors it to the bottom edge (with p-4 breathing room) — its
    // default "middle bottom" resting spot. Dragging then applies a transform on top of that
    // flow position, so the card can still be moved anywhere and stays clamped to this box.
    <div
      ref={constraintsRef}
      className="pointer-events-none fixed inset-0 z-9000 flex items-end justify-center p-4">
      <motion.div
        drag
        dragListener={false}
        dragControls={dragControls}
        dragMomentum={false}
        dragElastic={0}
        dragConstraints={constraintsRef}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn(
          'pointer-events-auto flex flex-col overflow-hidden rounded-xl',
          'bg-white shadow-2xl ring-1 ring-black/10 dark:bg-gray-900 dark:ring-white/15',
          className,
        )}
        data-test={dataTest}>
        <div
          onPointerDown={(e) => dragControls.start(e)}
          data-test="floating-widget-handle"
          // touch-none is required: without it the browser claims touch moves for scrolling and
          // the drag never starts on touch devices.
          className="flex cursor-grab touch-none items-center justify-between gap-2 pt-1 pr-1 pl-3 select-none active:cursor-grabbing">
          <Layout.GridDotsBlank className="size-4 shrink-0 text-gray-300 dark:text-gray-600" />
          {onClose && (
            <Button
              text
              size="sm"
              icon={<General.XMark />}
              tooltip={closeTooltip}
              aria-label={closeTooltip ?? 'Close'}
              // Keep a press on the X from starting a drag on the handle strip.
              onPointerDown={(e) => e.stopPropagation()}
              onClick={onClose}
              data-test="floating-widget-close"
            />
          )}
        </div>
        {children}
      </motion.div>
    </div>,
    target,
  );
}
